import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

type WelcomeMessageAudience = 'registered' | 'nonRegistered';

interface EmployeeFieldToken {
  token: string;
  label: string;
}

@Component({
  selector: 'app-system-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './system-settings.component.html',
  styleUrl: './system-settings.component.scss',
})
export class SystemSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('welcomeMessageModal') welcomeMessageModalRef!: ElementRef<HTMLElement>;
  @ViewChild('welcomeMessageTextarea') welcomeMessageTextareaRef!: ElementRef<HTMLTextAreaElement>;

  // Client info
  phoneNoId = '';
  wabaId = '';
  isLoadingClientInfo = false;
  clientInfoError = '';
  copyPhoneSuccess = false;
  copyWabaSuccess = false;

  // Welcome message
  isLoadingSettings = false;
  isSaving = false;
  errorMessage = '';

  selectedAudience: WelcomeMessageAudience | null = null;
  registeredMessage = '';
  nonRegisteredMessage = '';
  messageDraft = '';

  employeeFieldTokens: EmployeeFieldToken[] = [
    { token: '{{empName}}', label: 'Employee Name' },
    { token: '{{empNo}}', label: 'Employee No.' },
    { token: '{{department}}', label: 'Department' },
    { token: '{{role}}', label: 'Role' },
    { token: '{{bu}}', label: 'BU' },
    { token: '{{esiNo}}', label: 'ESI No.' },
    { token: '{{whatsappNumber}}', label: 'WhatsApp Number' },
    { token: '{{dob}}', label: 'Date of Birth' },
  ];

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.loadClientInfo();
  }

  ngAfterViewInit(): void {
    const modalEl = this.welcomeMessageModalRef?.nativeElement;
    if (modalEl) {
      document.body.appendChild(modalEl);
      modalEl.addEventListener('hide.bs.modal', () => {
        if (modalEl.contains(document.activeElement)) {
          (document.activeElement as HTMLElement)?.blur();
        }
      });
    }
  }

  ngOnDestroy(): void {
    const modalEl = this.welcomeMessageModalRef?.nativeElement;
    if (modalEl?.parentNode === document.body) {
      document.body.removeChild(modalEl);
    }
  }

  // ── Client info ───────────────────────────────────────────────────────────────

  loadClientInfo(): void {
    this.isLoadingClientInfo = true;
    this.clientInfoError = '';
    this.apiService.getApiCall('api/v1/systemsettings/client-info').subscribe({
      next: (res: any) => {
        this.isLoadingClientInfo = false;
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.phoneNoId = res.Data?.phoneNoId || '';
          this.wabaId = res.Data?.wabaId || '';
        } else {
          this.clientInfoError = res?.errorDescription || 'Unable to load client configuration.';
        }
      },
      error: () => {
        this.isLoadingClientInfo = false;
        this.clientInfoError = 'Unable to load client configuration. Please check your connection.';
      },
    });
  }

  copyToClipboard(text: string, field: 'phone' | 'waba'): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      if (field === 'phone') {
        this.copyPhoneSuccess = true;
        setTimeout(() => { this.copyPhoneSuccess = false; }, 2000);
      } else {
        this.copyWabaSuccess = true;
        setTimeout(() => { this.copyWabaSuccess = false; }, 2000);
      }
    });
  }

  // ── Welcome message ───────────────────────────────────────────────────────────

  openWelcomeMessageModal() {
    this.selectedAudience = null;
    this.errorMessage = '';
    this.loadWelcomeMessages();

    const modalEl = this.welcomeMessageModalRef?.nativeElement;
    if (!modalEl) return;
    const modalInstance = bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    modalInstance.show();
  }

  loadWelcomeMessages() {
    this.isLoadingSettings = true;
    this.apiService.getApiCall('api/v1/systemsettings/welcome-message').subscribe({
      next: (res: any) => {
        this.isLoadingSettings = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.registeredMessage = res.Data?.registeredMessage || '';
          this.nonRegisteredMessage = res.Data?.nonregisteredmsg || '';
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load the welcome message settings.';
        }
      },
      error: () => {
        this.isLoadingSettings = false;
        this.errorMessage = 'Unable to load the welcome message settings. Please check your connection.';
      },
    });
  }

  selectAudience(audience: WelcomeMessageAudience) {
    this.selectedAudience = audience;
    this.errorMessage = '';
    this.messageDraft = audience === 'registered' ? this.registeredMessage : this.nonRegisteredMessage;
  }

  backToOptions() {
    this.selectedAudience = null;
    this.errorMessage = '';
  }

  insertToken(token: string) {
    const textareaEl = this.welcomeMessageTextareaRef?.nativeElement;
    if (!textareaEl) {
      this.messageDraft += token;
      return;
    }

    const start = textareaEl.selectionStart ?? this.messageDraft.length;
    const end = textareaEl.selectionEnd ?? this.messageDraft.length;
    this.messageDraft = this.messageDraft.slice(0, start) + token + this.messageDraft.slice(end);

    setTimeout(() => {
      textareaEl.focus();
      const cursor = start + token.length;
      textareaEl.setSelectionRange(cursor, cursor);
    });
  }

  async saveWelcomeMessage() {
    if (!this.selectedAudience) return;
    this.isSaving = true;
    this.errorMessage = '';
    try {
      const res = await this.apiService.postApiCallReturningPromise('api/v1/systemsettings/welcome-message/update', {
        audience: this.selectedAudience,
        message: this.messageDraft,
      });
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        if (this.selectedAudience === 'registered') {
          this.registeredMessage = this.messageDraft;
        } else {
          this.nonRegisteredMessage = this.messageDraft;
        }
        this.selectedAudience = null;
      } else {
        this.errorMessage = res?.errorDescription || 'Unable to save the welcome message.';
      }
    } catch (err) {
      this.errorMessage = 'Unable to save the welcome message.';
    } finally {
      this.isSaving = false;
    }
  }
}
